/**
 * Keyboard Shortcuts Configuration
 *
 * Defines all global keyboard shortcuts used in the application.
 * These shortcuts are displayed in the Keyboard Shortcuts Modal
 * and used by the useKeyboardShortcuts hook.
 */

export interface KeyboardShortcut {
  /** Unique identifier for the shortcut */
  id: string

  /** Display name for the shortcut */
  name: string

  /** Description of what the shortcut does */
  description: string

  /** The key(s) to press. For display purposes (e.g., 'F9', 'Space', 'Escape') */
  keys: string[]

  /** Category for grouping in modal */
  category: 'TTS' | 'Navigation' | 'Accessibility' | 'General'

  /** Whether Ctrl/Cmd should be pressed */
  ctrlKey?: boolean

  /** Whether Alt/Option should be pressed */
  altKey?: boolean

  /** Whether Shift should be pressed */
  shiftKey?: boolean

  /** Whether Meta/Command should be pressed */
  metaKey?: boolean
}

export const KEYBOARD_SHORTCUTS: KeyboardShortcut[] = [
  // TTS Controls
  {
    id: 'tts-play-pause',
    name: 'Play/Pause Reading',
    description: 'Toggle playback of page narration',
    keys: ['F9'],
    category: 'TTS',
  },
  {
    id: 'tts-play-pause-alt',
    name: 'Play/Pause Reading (Alt)',
    description: 'Toggle playback of page narration (alternative)',
    keys: ['Space'],
    category: 'TTS',
  },
  {
    id: 'tts-skip-next',
    name: 'Skip Forward',
    description: 'Skip to the next section',
    keys: ['F8'],
    category: 'TTS',
  },
  {
    id: 'tts-skip-prev',
    name: 'Skip Back',
    description: 'Go back to the previous section',
    keys: ['F7'],
    category: 'TTS',
  },
  {
    id: 'tts-stop',
    name: 'Stop Reading',
    description: 'Stop reading and reset',
    keys: ['Escape'],
    category: 'TTS',
  },

  // Accessibility Controls
  {
    id: 'accessibility-toolbar-toggle',
    name: 'Toggle Accessibility Toolbar',
    description: 'Open or close the accessibility toolbar',
    keys: ['A'],
    altKey: true,
    category: 'Accessibility',
  },
]

/**
 * Get shortcuts by category
 */
export function getShortcutsByCategory(category: KeyboardShortcut['category']): KeyboardShortcut[] {
  return KEYBOARD_SHORTCUTS.filter((shortcut) => shortcut.category === category)
}

/**
 * Get all categories
 */
export function getAllCategories(): KeyboardShortcut['category'][] {
  const categories = new Set(KEYBOARD_SHORTCUTS.map((s) => s.category))
  return Array.from(categories)
}

/**
 * Format shortcut keys for display
 * E.g., ['Ctrl', 'Shift', 'P'] => 'Ctrl+Shift+P'
 */
export function formatShortcutKeys(shortcut: KeyboardShortcut): string {
  const modifiers = []

  // Mac uses ⌘ (Cmd) instead of Ctrl
  const isMac =
    typeof navigator !== 'undefined' && navigator.platform.toUpperCase().indexOf('MAC') >= 0

  if (shortcut.ctrlKey) {
    modifiers.push(isMac ? '⌘' : 'Ctrl')
  }
  if (shortcut.altKey) {
    modifiers.push(isMac ? '⌥' : 'Alt')
  }
  if (shortcut.shiftKey) {
    modifiers.push('⇧')
  }
  if (shortcut.metaKey) {
    modifiers.push('⌘')
  }

  return [...modifiers, ...shortcut.keys].join('+')
}
